import React, { useState } from 'react';
import {
  Box,
  Chip,
  Typography,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Divider
} from '@mui/material';
import {
  Download as DownloadIcon,
  Close as CloseIcon,
  PhotoSizeSelectActual as SizeIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { getAvailableDimensions, formatFileSize } from '../utils/imageGrouping';
import { getThumbnailUrl } from '../services/api';
import { downloadImage } from '../utils/downloadUtils';

const MotionBox = motion(Box);
const MotionChip = motion(Chip);

const DimensionSelector = ({
  imageGroup,
  selectedImage,
  onImageSelect,
  showInline = false,
  onClose = null,
  onDownloadError = null
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const availableDimensions = getAvailableDimensions(imageGroup);

  const handleImageSelect = (dimensionInfo) => {
    onImageSelect(dimensionInfo.image);
    if (dialogOpen) {
      setDialogOpen(false);
    }
  };

  const handleDownload = async (image, event) => {
    event.stopPropagation();
    await downloadImage(image, null, onDownloadError);
  };

  const handleDialogOpen = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = (event) => {
    if (event) {
      event.stopPropagation();
    }
    setDialogOpen(false);
    if (onClose) onClose();
  };

  if (!imageGroup || imageGroup.length <= 1) {
    return null;
  }

  const InlineSelector = () => (
    <MotionBox
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 10 }}
      sx={{
        position: 'absolute',
        bottom: 8,
        left: 8,
        right: 8,
        background: 'rgba(0, 0, 0, 0.8)',
        borderRadius: 1,
        p: 1,
        display: 'flex',
        flexWrap: 'wrap',
        gap: 0.5,
        alignItems: 'center',
        zIndex: 2
      }}
    >
      <Typography variant="caption" color="white" sx={{ mr: 1, flexShrink: 0 }}>
        {availableDimensions.length} sizes:
      </Typography>
      {availableDimensions.slice(0, 3).map((dim, index) => (
        <MotionChip
          key={`${dim.width}x${dim.height}`}
          label={dim.label}
          size="small"
          variant={selectedImage?.id === dim.image.id ? "filled" : "outlined"}
          color={selectedImage?.id === dim.image.id ? "primary" : "default"}
          onClick={(e) => {
            e.stopPropagation();
            handleImageSelect(dim);
          }}
          sx={{
            height: 20,
            fontSize: '0.7rem',
            color: selectedImage?.id === dim.image.id ? 'white' : 'rgba(255, 255, 255, 0.8)',
            borderColor: 'rgba(255, 255, 255, 0.3)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            }
          }}
          initial={{ scale: 0.8 }}
          animate={{ scale: 1 }}
          transition={{ delay: index * 0.1 }}
        />
      ))}
      {availableDimensions.length > 3 && (
        <Chip
          label={`+${availableDimensions.length - 3}`}
          size="small"
          variant="outlined"
          onClick={(e) => {
            e.stopPropagation();
            handleDialogOpen();
          }}
          sx={{
            height: 20,
            fontSize: '0.7rem',
            color: 'rgba(255, 255, 255, 0.8)',
            borderColor: 'rgba(255, 255, 255, 0.3)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            }
          }}
        />
      )}
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          handleDialogOpen();
        }}
        sx={{
          color: 'white',
          ml: 'auto',
          width: 24,
          height: 24
        }}
      >
        <SizeIcon fontSize="small" />
      </IconButton>
    </MotionBox>
  );

  const DetailedSelector = () => (
    <Dialog
      open={dialogOpen}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          handleDialogClose(event);
        }
      }}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SizeIcon />
          <Typography variant="h6">
            Choose Image Size
          </Typography>
        </Box>
        <IconButton onClick={(e) => handleDialogClose(e)}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select the image resolution that best fits your needs:
        </Typography>

        <Grid container spacing={2}>
          {availableDimensions.map((dim) => (
            <Grid item xs={12} sm={6} md={4} key={`${dim.width}x${dim.height}`}>
              <Card
                variant={selectedImage?.id === dim.image.id ? "elevation" : "outlined"}
                sx={{
                  cursor: 'pointer',
                  border: selectedImage?.id === dim.image.id ? 2 : 1,
                  borderColor: selectedImage?.id === dim.image.id ? 'primary.main' : 'divider',
                  '&:hover': {
                    boxShadow: 3,
                  }
                }}
                onClick={() => handleImageSelect(dim)}
              >
                <Box
                  component="img"
                  src={getThumbnailUrl(dim.image.thumbnail_file_path, dim.image.thumbnail_s3_url, dim.image.id)}
                  alt={dim.image.filename}
                  sx={{
                    width: '100%',
                    height: 120,
                    objectFit: 'cover'
                  }}
                />
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    {dim.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatFileSize(dim.image.file_size || 0)}
                  </Typography>
                  {selectedImage?.id === dim.image.id && (
                    <Chip
                      label="Current"
                      color="primary"
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  )}
                </CardContent>
                <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                  <Button
                    variant={selectedImage?.id === dim.image.id ? "contained" : "outlined"}
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleImageSelect(dim);
                    }}
                  >
                    {selectedImage?.id === dim.image.id ? "Selected" : "Select"}
                  </Button>
                  <Tooltip title="Download this size">
                    <IconButton
                      size="small"
                      onClick={(e) => handleDownload(dim.image, e)}
                    >
                      <DownloadIcon />
                    </IconButton>
                  </Tooltip>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoIcon color="action" fontSize="small" />
          <Typography variant="caption" color="text.secondary">
            These images contain the same content in different resolutions.
            Choose the size that best fits your intended use.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={(e) => handleDialogClose(e)}>
          Close
        </Button>
        {selectedImage && (
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={(e) => {
              handleDownload(selectedImage, e);
              handleDialogClose(e);
            }}
          >
            Download Selected
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );

  return (
    <>
      {showInline && <InlineSelector />}
      <DetailedSelector />
    </>
  );
};

export default DimensionSelector;
